<template>
  <div class="message-locales">
    <label class="block text-sm font-medium text-slate-700 mb-2">
      {{ $t('hit-admin.notifications.message-body') }}
    </label>

    <div v-if="loadingLocales" class="text-sm text-slate-500 mb-2">
      {{ $t('hit-admin.notifications.locales-loading') }}
    </div>

    <template v-else-if="localeTabs.length">
      <div class="locale-tabs">
        <button
          v-for="loc in localeTabs"
          :key="loc.id"
          type="button"
          class="locale-tab"
          :class="{active: activeLocale === loc.id}"
          @click="activeLocale = loc.id"
        >
          {{ loc.label }}
          <span v-if="loc.user_count != null" class="locale-count">
            ({{ loc.user_count }})
          </span>
        </button>
      </div>

      <hit-input-rich-text
        :key="activeLocale"
        :value="bodiesByLocale[activeLocale] || ''"
        :disabled="disabled"
        @input="onBodyChange"
      />
    </template>

    <div v-else class="text-sm text-slate-500">
      {{ $t('hit-admin.notifications.locales-empty') }}
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitInputRichText} from '@hit/components';
import {parseLocale} from '@hit/base/src/utils/language';
import {adminApi} from '@admin/services/adminApi';

const FALLBACK_LOCALES = [
  {id: 'fr-BE', label: 'Français (fr-BE)', user_count: 0},
];

function previewParamsKey(params) {
  if (!params) return '';
  return JSON.stringify({
    recipientType: params.recipientType,
    companyId: params.companyId ?? null,
    userId: params.userId ?? null,
    companyVerificationStatuses: params.companyVerificationStatuses ?? null,
    companyActiveOnly: params.companyActiveOnly ?? true,
  });
}

export default defineComponent({
  name: 'NotificationMessageLocales',
  components: {HitInputRichText},
  props: {
    previewParams: {type: Object, default: null},
    modelValue: {type: Object, default: () => ({})},
    disabled: {type: Boolean, default: false},
  },
  emits: ['update:modelValue'],
  data() {
    return {
      localeTabs: [],
      activeLocale: 'fr-BE',
      loadingLocales: false,
      bodiesByLocale: {...(this.modelValue || {})},
      loadTimer: null,
      lastLoadedKey: null,
    };
  },
  computed: {
    previewParamsKey() {
      return previewParamsKey(this.previewParams);
    },
  },
  watch: {
    previewParamsKey: {
      immediate: true,
      handler(key) {
        if (key === this.lastLoadedKey) return;
        this.scheduleLoadLocales();
      },
    },
    modelValue: {
      deep: true,
      handler(v) {
        const incoming = v || {};
        const current = this.bodiesByLocale || {};
        if (JSON.stringify(incoming) !== JSON.stringify(current)) {
          this.bodiesByLocale = {...incoming};
        }
      },
    },
  },
  beforeUnmount() {
    if (this.loadTimer) clearTimeout(this.loadTimer);
  },
  methods: {
    scheduleLoadLocales() {
      if (this.loadTimer) clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => this.loadLocales(), 400);
    },
    async loadLocales() {
      const key = this.previewParamsKey;
      if (key === this.lastLoadedKey && this.localeTabs.length) {
        return;
      }

      if (!this.previewParams) {
        this.localeTabs = [...FALLBACK_LOCALES];
        this.lastLoadedKey = key;
        this.ensureBodiesKeys();
        return;
      }
      if (
        this.previewParams.recipientType?.includes('company') &&
        !this.previewParams.companyId
      ) {
        this.localeTabs = [];
        this.lastLoadedKey = key;
        return;
      }
      this.loadingLocales = true;
      try {
        const res = await adminApi.previewNotificationLocales(
          this.previewParams
        );
        const locales = (res?.locales || []).map((loc) => {
          let label = loc.label || loc.id;
          try {
            const parsed = parseLocale(loc.id.replace(/-/g, '_'));
            label = parsed.longDesignation || loc.id;
          } catch {
            /* keep raw id */
          }
          return {
            id: loc.id,
            label: `${label} (${loc.id})`,
            user_count: loc.user_count,
          };
        });
        this.localeTabs = locales.length ? locales : [...FALLBACK_LOCALES];
        this.activeLocale =
          res?.default_locale || this.localeTabs[0]?.id || 'fr-BE';
        this.lastLoadedKey = key;
        this.ensureBodiesKeys();
      } catch {
        this.localeTabs = [...FALLBACK_LOCALES];
        this.lastLoadedKey = key;
        this.ensureBodiesKeys();
      } finally {
        this.loadingLocales = false;
      }
    },
    ensureBodiesKeys() {
      const next = {...this.bodiesByLocale};
      let changed = false;
      for (const loc of this.localeTabs) {
        if (next[loc.id] === undefined) {
          next[loc.id] = '';
          changed = true;
        }
      }
      if (changed) {
        this.bodiesByLocale = next;
        this.emitUpdate();
      }
    },
    onBodyChange(html) {
      this.bodiesByLocale = {
        ...this.bodiesByLocale,
        [this.activeLocale]: html || '',
      };
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('update:modelValue', {...this.bodiesByLocale});
    },
    hasContent() {
      return Object.values(this.bodiesByLocale || {}).some(
        (v) =>
          v &&
          String(v)
            .replace(/<[^>]*>/g, '')
            .trim().length > 0
      );
    },
  },
});
</script>

<style lang="scss" scoped>
.locale-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
}

.locale-tab {
  padding: 0.35rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: white;
  cursor: pointer;
  &.active {
    background: #00997c;
    color: white;
    border-color: #00997c;
  }
}

.locale-count {
  opacity: 0.85;
  font-size: 0.75rem;
}
</style>
